/**
 * JurisdictionResearchLinksTab (RESEARCH-LINKS-001 / UI-004)
 * Admin/JM editor for a jurisdiction's research links — rendered as a tab
 * inside JurisdictionWizardModal (edit mode). Standard category slots +
 * custom links + inline category registry management. All saves go through
 * PUT /api/jurisdictions/{id}/research-links (audited server-side).
 * Save Links lives in the modal footer (reported via `onToolbar`).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Link2, Plus, Loader2, ChevronDown, ChevronRight, MoreVertical,
  ScrollText, Home, Map, Landmark, Gavel, Scale, FileSearch, Settings2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

export const CATEGORY_ICONS = {
  recorder_deeds: ScrollText,
  assessor: Home,
  gis_parcel: Map,
  tax_portal: Landmark,
  court_judgment: Gavel,
  probate: Scale,
  ucc_state: FileSearch,
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// User-friendly entry: trim + auto-prefix https:// for bare domains
// (www.example.com / example.com). Validation happens AFTER normalization.
const normalizeUrl = (u) => {
  let s = (u || '').trim();
  if (!s) return '';
  if (!/^https?:\/\//i.test(s)) s = `https://${s}`;
  return s;
};
const isAcceptableUrl = (u) =>
  /^https?:\/\/[a-z0-9]([a-z0-9\-.]*[a-z0-9])?\.[a-z]{2,}(:\d+)?(\/\S*)?$/i.test(normalizeUrl(u));

const JurisdictionResearchLinksTab = ({ jurisdiction, onToolbar }) => {
  const jid = jurisdiction?.id;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [standard, setStandard] = useState({}); // category_id -> {id, url}
  const [customs, setCustoms] = useState([]);   // [{id?, label, url}]
  const [dirty, setDirty] = useState(false);
  const [showCatManager, setShowCatManager] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState('');
  const [addingCat, setAddingCat] = useState(false);
  const saveRef = useRef(null);

  const load = useCallback(async () => {
    if (!jid) return;
    setLoading(true);
    try {
      const [catsRes, linksRes] = await Promise.all([
        axios.get(`${API}/api/research-link-categories`, { headers: authHeaders() }),
        axios.get(`${API}/api/jurisdictions/${jid}/research-links`, { headers: authHeaders() }),
      ]);
      setCategories(catsRes.data.categories || []);
      const std = {};
      const cst = [];
      (linksRes.data.links || []).forEach((l) => {
        if (l.category_id) std[l.category_id] = { id: l.id, url: l.url || '' };
        else cst.push({ id: l.id, label: l.label || '', url: l.url || '' });
      });
      setStandard(std);
      setCustoms(cst);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load research links');
    } finally {
      setLoading(false);
    }
  }, [jid]);

  useEffect(() => { load(); }, [load]);

  const setStdUrl = (catId, url) => {
    setStandard((prev) => ({ ...prev, [catId]: { ...(prev[catId] || {}), url } }));
    setDirty(true);
  };

  const save = async () => {
    const links = [];
    const errors = [];
    categories.forEach((c) => {
      const row = standard[c.id];
      const raw = (row?.url || '').trim();
      if (!raw) return;
      if (!isAcceptableUrl(raw)) errors.push(`${c.label}: enter a valid website (e.g. www.example.com)`);
      links.push({ id: row.id || null, category_id: c.id, url: normalizeUrl(raw), is_active: true });
    });
    customs.forEach((c, i) => {
      const label = (c.label || '').trim();
      const raw = (c.url || '').trim();
      if (!label && !raw) return;
      if (!label) errors.push(`Custom link ${i + 1}: label is required`);
      if (!isAcceptableUrl(raw)) errors.push(`Custom link ${i + 1}: enter a valid website (e.g. www.example.com)`);
      links.push({ id: c.id || null, category_id: null, label, url: normalizeUrl(raw), is_active: true });
    });
    if (errors.length) {
      toast.error(errors[0]);
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/api/jurisdictions/${jid}/research-links`, { links }, { headers: authHeaders() });
      toast.success('Research links saved');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save research links');
    } finally {
      setSaving(false);
    }
  };
  saveRef.current = save;

  // Footer toolbar (Save Links) is rendered by the modal so the footer stays stable.
  useEffect(() => {
    if (!onToolbar) return;
    onToolbar({ dirty, saving, loading, save: () => saveRef.current && saveRef.current() });
  }, [dirty, saving, loading, onToolbar]);

  const addCategory = async () => {
    if (!newCatLabel.trim()) return;
    setAddingCat(true);
    try {
      await axios.post(`${API}/api/research-link-categories`, { label: newCatLabel.trim() }, { headers: authHeaders() });
      toast.success('Category added');
      setNewCatLabel('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add category');
    } finally {
      setAddingCat(false);
    }
  };

  const deactivateCategory = async (cat) => {
    try {
      await axios.patch(`${API}/api/research-link-categories/${cat.id}`, { is_active: false }, { headers: authHeaders() });
      toast.success(`Category "${cat.label}" deactivated`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to deactivate category');
    }
  };

  const updateCustom = (i, patch) => {
    setCustoms((p) => p.map((x, xi) => (xi === i ? { ...x, ...patch } : x)));
    setDirty(true);
  };

  if (loading) {
    return (
      <div className="jw-loading" data-testid="research-links-tab-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading research links…
      </div>
    );
  }

  return (
    <div className="jw-panel space-y-4" data-testid="research-links-tab">
      {/* Standard category slots */}
      <div className="jw-list" data-testid="research-links-standard">
        {categories.map((c) => {
          const Icon = CATEGORY_ICONS[c.key] || Link2;
          const url = standard[c.id]?.url || '';
          return (
            <div key={c.id} className="jw-row" data-testid={`research-link-row-${c.key}`}>
              <div className="jw-row-label">
                <span className="jw-row-icon"><Icon /></span>
                <span className="jw-row-text">{c.label}</span>
              </div>
              <Input
                value={url}
                onChange={(e) => setStdUrl(c.id, e.target.value)}
                placeholder="https://www.example.com"
                className={`jw-row-input ${url && !isAcceptableUrl(url) ? 'jw-input-invalid' : ''}`}
                data-testid={`research-link-input-${c.key}`}
              />
            </div>
          );
        })}
      </div>

      {/* Custom links */}
      <div data-testid="research-links-custom">
        <div className="jw-section-head">
          <span className="jw-section-title">Custom Links</span>
          <button
            type="button"
            className="jw-btn-ghost jw-btn-sm"
            onClick={() => { setCustoms((p) => [...p, { label: '', url: '' }]); setDirty(true); }}
            data-testid="add-custom-link-btn"
          >
            <Plus /> Add Link
          </button>
        </div>
        {customs.length > 0 && (
          <div className="jw-list">
            {customs.map((c, i) => (
              <div key={c.id || `new-${i}`} className="jw-row" data-testid={`custom-link-row-${i}`}>
                <div className="jw-row-label">
                  <span className="jw-row-icon"><Link2 /></span>
                  <Input
                    value={c.label}
                    onChange={(e) => updateCustom(i, { label: e.target.value })}
                    placeholder="Label (e.g. Zoning Department)"
                    className="jw-row-input jw-row-input-label"
                    data-testid={`custom-link-label-${i}`}
                  />
                </div>
                <Input
                  value={c.url}
                  onChange={(e) => updateCustom(i, { url: e.target.value })}
                  placeholder="https://www.example.com"
                  className={`jw-row-input ${c.url && !isAcceptableUrl(c.url) ? 'jw-input-invalid' : ''}`}
                  data-testid={`custom-link-url-${i}`}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="jw-row-menu" aria-label="Custom link actions" data-testid={`custom-link-menu-${i}`}>
                      <MoreVertical />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[140px]">
                    <DropdownMenuItem
                      className="text-rose-600 focus:text-rose-700 text-xs"
                      onClick={() => { setCustoms((p) => p.filter((_, xi) => xi !== i)); setDirty(true); }}
                      data-testid={`custom-link-remove-${i}`}
                    >
                      Remove link
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Category registry manager */}
      <div className="jw-collapse">
        <button
          type="button"
          className="jw-collapse-toggle"
          onClick={() => setShowCatManager((v) => !v)}
          data-testid="toggle-category-manager"
        >
          {showCatManager ? <ChevronDown /> : <ChevronRight />}
          <Settings2 /> Manage Categories
        </button>
        {showCatManager && (
          <div className="px-3 pb-3 space-y-2" data-testid="category-manager">
            <div className="flex items-center gap-2">
              <Input
                value={newCatLabel}
                onChange={(e) => setNewCatLabel(e.target.value)}
                placeholder="New category label (applies to all jurisdictions)"
                className="jw-row-input"
                data-testid="new-category-input"
              />
              <Button size="sm" onClick={addCategory} disabled={addingCat || !newCatLabel.trim()} data-testid="new-category-add-btn">
                {addingCat ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {categories.map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-white border-slate-200 text-slate-600" data-testid={`category-chip-${c.key}`}>
                  {c.label}
                  {!c.is_standard && (
                    <button type="button" onClick={() => deactivateCategory(c)} className="text-slate-300 hover:text-rose-500 ml-0.5" data-testid={`category-deactivate-${c.key}`}>×</button>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JurisdictionResearchLinksTab;
